import { useEffect, useState } from 'react';
import { loginWithKakaoCode } from '../../api/auth/authApi';
import { setAuthTokens } from '../../auth/tokenStorage';

const PENDING_KAKAO_SIGNUP_KEY = 'pendingKakaoSignup';
const PENDING_SOCIAL_CONSENT_KEY = 'pendingSocialConsent';
const KAKAO_BACK_GUARD_SESSION_KEY = 'ahshootKakaoBackGuardEnabled';
const SOCIAL_CONSENT_MAX_AGE_MS = 10 * 60 * 1000;
const kakaoLoginRequestByCode = new Map();

function getNewUserFlag(responseData) {
  return responseData?.newUser === true;
}

function getResponseField(responseData, fieldName) {
  return responseData?.[fieldName] ?? null;
}

function hasResponseField(responseData, fieldName) {
  if (!responseData || typeof responseData !== 'object') {
    return false;
  }
  return responseData[fieldName] !== undefined;
}

function getResponseBoolean(responseData, fieldName, fallback = false) {
  const value = getResponseField(responseData, fieldName);
  return typeof value === 'boolean' ? value : fallback;
}

function sanitizeSocialConsentErrorMessage(error) {
  const rawMessage = String(error?.message || '').trim();
  if (
    error?.status === 400 &&
    (rawMessage.includes('필수 동의') ||
      rawMessage.includes('약관') ||
      rawMessage.includes('개인정보'))
  ) {
    return '필수 약관 동의가 확인되지 않았습니다. 로그인 화면에서 다시 동의해 주세요.';
  }
  return rawMessage || '로그인 처리 중 오류가 발생했습니다.';
}

function readPendingSocialConsent() {
  const rawValue = sessionStorage.getItem(PENDING_SOCIAL_CONSENT_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue);
    const createdAt = Number(parsed?.createdAt);
    const isExpired =
      !Number.isFinite(createdAt) || Date.now() - createdAt > SOCIAL_CONSENT_MAX_AGE_MS;

    if (isExpired) {
      return null;
    }

    return {
      agreedTerms: parsed?.agreedTerms === true,
      agreedPrivacy: parsed?.agreedPrivacy === true,
      agreedMarketing: parsed?.agreedMarketing === true,
    };
  } catch (error) {
    return null;
  }
}

// StrictMode(개발 모드)에서 effect가 2회 실행되어도 code 교환 요청은 1회만 보낸다.
function requestKakaoLoginOnce({ code, consent }) {
  const cachedRequest = kakaoLoginRequestByCode.get(code);
  if (cachedRequest) {
    return cachedRequest;
  }

  const requestPromise = loginWithKakaoCode({
    code,
    agreedTerms: consent.agreedTerms,
    agreedPrivacy: consent.agreedPrivacy,
    agreedMarketing: consent.agreedMarketing,
  }).finally(() => {
    kakaoLoginRequestByCode.delete(code);
  });
  kakaoLoginRequestByCode.set(code, requestPromise);
  return requestPromise;
}

function KakaoCallbackPage() {
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('카카오 로그인 처리 중입니다...');

  useEffect(() => {
    let isActive = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const oauthError = params.get('error');

    if (oauthError) {
      sessionStorage.removeItem(PENDING_SOCIAL_CONSENT_KEY);
      sessionStorage.removeItem(KAKAO_BACK_GUARD_SESSION_KEY);
      setStatus('error');
      setMessage('카카오 인증이 취소되었거나 실패했습니다.');
      return;
    }

    if (!code) {
      sessionStorage.removeItem(PENDING_SOCIAL_CONSENT_KEY);
      sessionStorage.removeItem(KAKAO_BACK_GUARD_SESSION_KEY);
      setStatus('error');
      setMessage('인가 코드(code)를 찾을 수 없습니다.');
      return;
    }

    const consent = readPendingSocialConsent();
    if (!consent?.agreedTerms || !consent?.agreedPrivacy) {
      sessionStorage.removeItem(PENDING_SOCIAL_CONSENT_KEY);
      sessionStorage.removeItem(KAKAO_BACK_GUARD_SESSION_KEY);
      setStatus('error');
      setMessage('동의 정보가 없거나 만료되었습니다. 동의 후 다시 시도해 주세요.');
      setTimeout(() => {
        if (isActive) {
          window.location.replace('/auth/login');
        }
      }, 1100);
      return;
    }

    const login = async () => {
      try {
        const responseData = await requestKakaoLoginOnce({ code, consent });
        if (!isActive) {
          return;
        }
        const accessToken = responseData?.accessToken;
        const refreshToken = responseData?.refreshToken;
        const isNewUser = getNewUserFlag(responseData);
        const email = getResponseField(responseData, 'email');
        const hasAgreedTermsField = hasResponseField(responseData, 'agreedTerms');
        const hasAgreedPrivacyField = hasResponseField(responseData, 'agreedPrivacy');
        const shouldCheckConsentInResponse =
          hasAgreedTermsField && hasAgreedPrivacyField;
        const agreedTerms = getResponseBoolean(responseData, 'agreedTerms', true);
        const agreedPrivacy = getResponseBoolean(responseData, 'agreedPrivacy', true);
        const requiresSignupByConsent =
          shouldCheckConsentInResponse && (!agreedTerms || !agreedPrivacy);

        if (!accessToken || !refreshToken) {
          throw new Error('인증 토큰 응답이 올바르지 않습니다.');
        }
        if (!email) {
          throw new Error('카카오 이메일 동의가 필요합니다. 동의 후 다시 로그인해 주세요.');
        }

        setAuthTokens({ accessToken, refreshToken });
        sessionStorage.setItem(KAKAO_BACK_GUARD_SESSION_KEY, '1');

        if (isNewUser || requiresSignupByConsent) {
          sessionStorage.setItem(
            PENDING_KAKAO_SIGNUP_KEY,
            JSON.stringify({
              userId: getResponseField(responseData, 'userId'),
              email,
              nickname: getResponseField(responseData, 'nickname'),
              profileImageUrl: getResponseField(responseData, 'profileImageUrl'),
              accessToken,
              refreshToken,
            }),
          );

          setStatus('success');
          setMessage('신규 회원으로 확인되었습니다. 회원가입 화면으로 이동합니다.');
          setTimeout(() => {
            window.location.replace('/auth/signup');
          }, 800);
          return;
        }

        setStatus('success');
        setMessage('로그인 성공! 잠시 후 이동합니다.');
        setTimeout(() => {
          window.location.replace('/team');
        }, 1200);
      } catch (error) {
        if (!isActive) {
          return;
        }
        setStatus('error');
        setMessage(sanitizeSocialConsentErrorMessage(error));
      } finally {
        sessionStorage.removeItem(PENDING_SOCIAL_CONSENT_KEY);
      }
    };

    login();
    return () => {
      isActive = false;
    };
  }, []);

  return (
    <main className="grid min-h-screen place-items-center bg-slate-50 p-6">
      <section className="w-full max-w-[480px] rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
        <h1 className="m-0 text-xl font-semibold text-slate-900">Ah Shoot 로그인</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">{message}</p>
        {status === 'loading' && (
          <div className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full w-1/2 animate-pulse rounded-full bg-slate-500" />
          </div>
        )}
        {status === 'error' && (
          <button
            type="button"
            className="mt-5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
            onClick={() => window.location.replace('/auth/login')}
          >
            로그인 화면으로 돌아가기
          </button>
        )}
      </section>
    </main>
  );
}

export default KakaoCallbackPage;
